import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as runtime from 'types/backend';

export interface User {
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  role_inherited: string;
  user_id: string;
}

export type Users = User[];

const permissionsUrl = __PERMISSIONS_SERVICE_URL__;
const createRequestUrl = (
  projectId: string,
  entityType: string,
  entityId: string
) =>
  `${permissionsUrl}/projects/${projectId}/${entityType}s${
    entityId ? `/${entityId}` : ''
  }`;

const headerParameters: runtime.HTTPHeaders = {};

const fetchUsers = async (
  projectId: string,
  entityType: string,
  entityId: string
) => {
  try {
    const requestUrl = createRequestUrl(projectId, entityType, entityId);
    const response = await fetch(requestUrl, {
      credentials: 'include',
      headers: headerParameters,
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error(
        JSON.stringify({
          message: `Fetching users for project ${projectId} failed.
          ${response.status}`,
        })
      );
    }

    const result = await response.json();
    return result.users;
  } catch (error) {
    throw new Error(
      JSON.stringify({
        message: `Fetching users for project ${projectId} failed.`,
        ...(error as any),
      })
    );
  }
};
export const getUsersQueryService = (fetchParams: {
  entityId: string;
  entityType: string;
  projectId: string;
}) => ({
  fetch: async (): Promise<any> => {
    return await fetchUsers(
      fetchParams.projectId,
      fetchParams.entityType,
      fetchParams.entityId
    );
  },
});

export interface UsersDataProps {
  error: Error;
  fetchUserParams: { entityId: string; entityType: string; projectId: string };
  isFetchingUsers: boolean;
  isLoadingUsers: boolean;
  updateFetchUserParams: (
    args?: Partial<{ entityId: string; entityType: string; projectId: string }>
  ) => void;
  usersData: Users;
}

function useUsersData({
  projectId,
  entityType,
  entityId,
}: {
  entityId: string;
  entityType: string;
  projectId: string;
}): UsersDataProps {
  if (!projectId || !entityType || !entityId) {
    throw new Error(
      'The project_id ,entityType and entityId params are required for useUsersData.'
    );
  }
  const QUERY_KEY: string[] = [`users`, String(projectId)];
  const INITIAL_PARAMS = useMemo(
    () => ({
      entityId,
      entityType,
      projectId,
    }),
    [projectId, entityType, entityId]
  );

  const [fetchParams, setFetchParams] = useState(INITIAL_PARAMS);

  const queryService = useMemo(() => getUsersQueryService(fetchParams), [
    fetchParams,
  ]);

  const { isFetching, isLoading, data: usersData, error } = useQuery({
    keepPreviousData: true,
    queryFn: queryService.fetch,
    queryKey: QUERY_KEY,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const updateFetchUserParams = useCallback(
    (
      params?: Partial<{
        entityId: string;
        entityType: string;
        projectId: string;
      }>
    ) => setFetchParams(prevParams => ({ ...prevParams, ...params })),
    []
  );

  return {
    error: error as Error,
    fetchUserParams: fetchParams,
    isFetchingUsers: isFetching,
    isLoadingUsers: isLoading,
    updateFetchUserParams,
    usersData: usersData || [],
  };
}

export default useUsersData;
